import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { CARNET_ACCENTS } from "@/data/constants";
import { FoundationFeature } from "@/components/FoundationFeature";
import { SectionHead } from "@/pages/Home";
import { ArrowRight, ShoppingCart } from "lucide-react";

const purchaseLinks = [
  { key: "amazon_url", label: "ACHETER SUR AMAZON" },
  { key: "kindle_url", label: "VERSION KINDLE" },
  { key: "pdf_url", label: "VERSION PDF" },
  { key: "ebook_url", label: "VERSION EBOOK" },
  { key: "other_url", label: "AUTRE VERSION" },
];

export default function Carnets() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/books")
      .then((r) => setBooks(r.data.filter((b) => b.number >= 1)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="bg-[#F8F5EE] border-b border-[#E8DCC4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-16 md:py-20 text-center">
          <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">
            Collection officielle
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#1F2A24] mt-3">Les Carnets 21 Jours</h1>
          <div className="gold-divider w-24 mx-auto my-6" />
          <p className="font-sans text-base text-[#1F2A24]/80">
            Dix carnets pour expérimenter la méthode dans votre vie quotidienne, un chemin après
            l'autre.
          </p>
        </div>
      </section>

      <FoundationFeature />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20">
        {loading ? (
          <p data-testid="carnets-loading" className="text-center font-sans text-[#1F2A24]/60">Chargement…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="carnets-grid">
            {books.map((b, i) => (
              <CarnetCard key={b.id} book={b} accent={CARNET_ACCENTS[i % CARNET_ACCENTS.length]} />
            ))}
          </div>
        )}
      </section>

      {/* Passerelle vers l'Aventure */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 pb-16">
        <div data-testid="bridge-carnet-aventure" className="rounded-2xl border border-[#C9A24B]/30 bg-[#1F2A24] p-7 md:p-8 text-center text-[#F6F2E9]">
          <p className="font-serif italic text-lg text-[#F6F2E9]/85">Envie de vivre l'histoire de l'Île de l'Abondance ?</p>
          <Link
            to="/univers/ile-de-labondance"
            data-testid="bridge-to-aventure"
            className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm mt-4 hover:bg-[#2c4a2e] transition-colors"
          >
            Découvrir l'Aventure <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

const CarnetCard = ({ book, accent }) => {
  const available = purchaseLinks.filter((p) => book[p.key]);
  return (
    <div
      data-testid={`carnet-card-${book.number}`}
      className="rounded-2xl bg-white border border-[#E8DCC4] overflow-hidden flex flex-col"
    >
      <div
        className="aspect-[16/9] relative overflow-hidden"
        style={{ background: `linear-gradient(150deg, ${accent}, ${accent}cc)` }}
      >
        {book.cover && (
          <img src={book.cover} alt={book.title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(to top, rgba(31,42,36,0.75), rgba(31,42,36,0.10))" }}
        />
        <div className="relative h-full flex flex-col justify-between p-5">
          <span className="font-sans text-xs tracking-[0.2em] text-white/90">
            CARNET {String(book.number).padStart(2, "0")}
          </span>
          <span className="font-serif text-2xl text-white leading-tight drop-shadow-md">{book.title}</span>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <span
          className="inline-block self-start rounded-full px-3 py-1 font-ui text-[11px] tracking-wider"
          style={{ backgroundColor: `${accent}1A`, color: accent }}
        >
          {book.function}
        </span>
        <p className="font-sans text-sm text-[#1F2A24]/75 mt-3 leading-relaxed flex-1">
          {book.description}
        </p>

        <div className="mt-5">
          {book.active ? (
            <>
              <Link
                to={`/carnet/${book.id}`}
                data-testid={`carnet-open-${book.number}`}
                className="inline-flex w-full justify-center items-center gap-2 rounded-full bg-[#385E3A] text-white px-5 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
              >
                COMMENCER LE PARCOURS · 21 JOURS <ArrowRight size={16} />
              </Link>
            </>
          ) : (
            <span
              data-testid={`carnet-soon-${book.number}`}
              className="inline-block w-full text-center rounded-full bg-[#E8DCC4] text-[#1F2A24]/60 px-5 py-3 font-ui text-sm"
            >
              BIENTÔT DISPONIBLE
            </span>
          )}
              {available.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {available.map((p) => (
                    <a
                      key={p.key}
                      href={book[p.key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`carnet-${book.number}-${p.key}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#C9A24B] text-[#1F2A24] px-3 py-1.5 font-ui text-[11px] hover:bg-[#C9A24B]/10 transition-colors"
                    >
                      <ShoppingCart size={13} /> {p.label}
                    </a>
                  ))}
                </div>
              )}
        </div>
      </div>
    </div>
  );
};
